import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { api, ApiError, brandTheme } from "@tc/ui";
import { AuthFrame } from "../security/AuthFrame.js";
import { biometricName, cancelled, passkeySupported, signInWithPasskey } from "../security/passkey.js";
import { PinPad } from "../security/PinPad.js";
import { showWelcome } from "../security/welcome.js";
import { Finder } from "./Finder.js";
export function Login({ onSignedIn }) {
    const [company, setCompany] = useState(null);
    const [missing, setMissing] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [challenge, setChallenge] = useState(null);
    /** The second step being asked for (26/10/26): the app's code, or a code sent by text / email, and the other ways. */
    const [step, setStep] = useState({ method: "totp", alternatives: [] });
    const [resent, setResent] = useState(0);
    // After the password, for people whose second step is Face ID (audit 26/10/26): the ticket for that Face ID.
    const [faceTicket, setFaceTicket] = useState(null);
    const [code, setCode] = useState("");
    const [device, setDevice] = useState(null);
    const [full, setFull] = useState(false);
    const [reset, setReset] = useState(0);
    const [forgot, setForgot] = useState(false);
    const [sent, setSent] = useState(null);
    useEffect(() => {
        const fromLink = new URLSearchParams(window.location.search).get("email");
        if (fromLink) {
            setEmail(fromLink);
            setFull(true);
        }
        api("/api/company").then((c) => {
            setCompany(c);
            const theme = c.branding.defaultTheme ?? "light";
            document.documentElement.dataset.theme = theme;
            for (const [k, v] of Object.entries(brandTheme(c.branding, theme)))
                document.documentElement.style.setProperty(k, v);
        }, () => setMissing(true));
        api("/api/auth/device").then(setDevice, () => setDevice({ known: false }));
    }, []);
    // In: the company's welcome plays over the move into the app (security/welcome.ts).
    const signedIn = () => { showWelcome(company, device?.name?.split(" ")[0]); onSignedIn(); };
    const unlockPin = async (pin) => {
        setBusy(true);
        setError(null);
        try {
            await api("/api/auth/pin", { body: { pin } });
            signedIn();
        }
        catch (e) {
            setError(e.message);
            setReset((n) => n + 1);
            if (e instanceof ApiError && e.code === "full-signin") {
                setDevice((d) => d && { ...d, needsFull: true });
                setFull(true);
            }
        }
        setBusy(false);
    };
    const passkey = async (ticket) => {
        setBusy(true);
        setError(null);
        try {
            await signInWithPasskey(ticket);
            signedIn();
        }
        catch (e) {
            if (!cancelled(e))
                setError(e.message);
            // The ticket is used once: an expired or failed one means signing in again.
            if (ticket && !cancelled(e)) {
                setFaceTicket(null);
                setPassword("");
            }
        }
        setBusy(false);
    };
    async function submit(e) {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
            if (forgot) {
                const r = await api("/api/auth/forgot", { body: { email } });
                setSent(r.message);
                return;
            }
            if (challenge) {
                await api("/api/auth/login/code", { method: "POST", body: { challenge, code } });
                signedIn();
                return;
            }
            const r = await api("/api/auth/login", { method: "POST", body: { email, password } });
            if (r.needsCode && r.challenge) {
                setChallenge(r.challenge);
                setStep({ method: r.method ?? "totp", alternatives: r.alternatives ?? [], sentTo: r.sentTo, note: r.note });
                setPassword("");
                return;
            }
            if (r.needsPasskey && r.challenge) {
                setFaceTicket(r.challenge);
                setPassword("");
                return;
            }
            signedIn();
        }
        catch (err) {
            setError(err.message);
        }
        finally {
            setBusy(false);
        }
    }
    /** Send (or send again) a sign-in code by text or email for this ticket. */
    async function sendVia(via) {
        if (!challenge)
            return;
        setBusy(true);
        setError(null);
        try {
            const r = await api("/api/auth/login/send", { body: { challenge, via } });
            setStep({ method: via, alternatives: [step.method, ...step.alternatives].filter((m, i, a) => m !== via && a.indexOf(m) === i), sentTo: r.sentTo });
            setResent(Date.now());
            setCode("");
        }
        catch (e) {
            setError(e.message);
        }
        finally {
            setBusy(false);
        }
    }
    // The bare Nozl address (or a company that doesn't exist): help them find theirs.
    if (missing)
        return _jsx(Finder, {});
    if (!device)
        return _jsx("div", { className: "tc-boot", "aria-busy": "true" });
    const unlock = device?.known && !device.needsFull && !full && !challenge && (device.pin || (device.passkey && passkeySupported()));
    const toPassword = () => { setFull(true); setError(null); };
    // Every screen here is the same frame (redesigned 27/10/26): the company's logo and colour on the band, the step on
    // the white panel under one plain heading, "Powered by nozl" at the foot.
    const brandRow = (line) => (_jsx("div", { className: "tc-auth-hello", children: _jsx("h1", { children: line }) }));
    if (unlock) {
        const bio = device.passkey && passkeySupported();
        return (_jsx(AuthFrame, { pin: true, company: company, children: _jsxs("div", { className: "tc-auth-form tc-auth-pinbox", children: [_jsx("p", { className: "tc-sign-who", children: device.email ?? company?.name }), error && _jsx("div", { className: "tc-error", role: "alert", children: error }), device.pin
                        ? _jsx(PinPad, { busy: busy, resetKey: reset, label: "Enter your PIN", onComplete: (p) => void unlockPin(p), onBiometric: bio ? () => void passkey() : undefined, biometricLabel: `Unlock with ${biometricName()}` })
                        : bio && _jsxs("button", { className: "tc-btn primary", disabled: busy, onClick: () => void passkey(), children: ["Unlock with ", biometricName()] }), _jsx("button", { type: "button", className: "tc-link tc-auth-alt", onClick: toPassword, children: "Use password instead" })] }) }));
    }
    return (_jsx(AuthFrame, { company: company, children: _jsxs("form", { className: "tc-auth-form", onSubmit: submit, children: [brandRow(forgot ? "Set a new password" : faceTicket ? `Confirm with ${biometricName()}` : challenge ? "Enter your code" : "Sign in"), error && _jsx("div", { className: "tc-error", role: "alert", children: error }), device?.needsFull && !challenge && !forgot && _jsx("div", { className: "tc-warn-bar", children: _jsx("span", { children: device.reason === "locked" ? "For your security, sign in with your password: the PIN was entered wrongly too many times." : `For your security, sign in with your password every ${device.days ?? 30} days.` }) }), forgot ? _jsxs(_Fragment, { children: [sent ? _jsx("div", { className: "tc-ok", role: "status", children: sent }) : _jsx("p", { className: "tc-m0", children: "Enter your email and we'll send you a link to set a new password." }), !sent && _jsxs("label", { className: "tc-field", children: ["Email", _jsx("input", { className: "tc-input", type: "email", autoComplete: "username", autoFocus: true, value: email, onChange: (e) => setEmail(e.target.value), required: true })] }), !sent && _jsx("button", { className: "tc-btn primary", type: "submit", disabled: busy, children: busy ? "Sending…" : "Send me a link" }), _jsx("button", { type: "button", className: "tc-link", onClick: () => { setForgot(false); setSent(null); setError(null); }, children: "Back to sign in" })] }) : faceTicket ? _jsxs(_Fragment, { children: [_jsxs("p", { className: "tc-m0", children: ["Now confirm it's you with ", biometricName(), ", as you set up for this company."] }), _jsx("button", { type: "button", className: "tc-btn primary", autoFocus: true, disabled: busy, onClick: () => void passkey(faceTicket), children: busy ? "Checking…" : `Continue with ${biometricName()}` }), _jsx("button", { type: "button", className: "tc-link", onClick: () => { setFaceTicket(null); setError(null); }, children: "Back" })] }) : challenge ? _jsxs(_Fragment, { children: [_jsx("p", { className: "tc-m0", children: step.method === "sms" ? _jsxs(_Fragment, { children: ["We've texted a 6-digit code to ", _jsx("b", { children: step.sentTo }), ". It works for 5 minutes."] })
                                : step.method === "email" ? _jsxs(_Fragment, { children: ["We've emailed a 6-digit code to ", _jsx("b", { children: step.sentTo }), ". It works for 5 minutes; check your junk folder if it isn't there."] })
                                    : "The 6-digit code from your authenticator app. Lost your phone? Use one of your recovery codes." }), step.note && step.method !== "totp" && _jsx("p", { className: "tc-help tc-m0", children: step.note }), _jsxs("label", { className: "tc-field", children: ["Code", _jsx("input", { className: "tc-input tc-code-input", inputMode: "numeric", autoComplete: "one-time-code", autoFocus: true, value: code, onChange: (e) => setCode(e.target.value), required: true, maxLength: 12 })] }), _jsx("button", { className: "tc-btn primary", type: "submit", disabled: busy || code.trim().length < 6, children: busy ? "Checking…" : "Continue" }), _jsxs("div", { className: "tc-code-ways", children: [step.method !== "totp" && _jsx("button", { type: "button", className: "tc-link", disabled: busy || Date.now() - resent < 30_000, onClick: () => void sendVia(step.method), children: "Send it again" }), step.alternatives.map((m) => (_jsx("button", { type: "button", className: "tc-link", disabled: busy, onClick: () => (m === "totp" ? setStep({ ...step, method: "totp", alternatives: [step.method, ...step.alternatives.filter((x) => x !== "totp")] }) : void sendVia(m)), children: m === "totp" ? "Use my authenticator app" : m === "sms" ? "Text me a code instead" : "Email me a code instead" }, m)))] }), _jsx("button", { type: "button", className: "tc-link", onClick: () => { setChallenge(null); setCode(""); setError(null); }, children: "Back" })] }) : _jsxs(_Fragment, { children: [_jsxs("label", { className: "tc-field", children: ["Email", _jsx("input", { className: "tc-input", type: "email", autoComplete: "username", value: email, onChange: (e) => setEmail(e.target.value), required: true })] }), _jsxs("label", { className: "tc-field", children: ["Password", _jsx("input", { className: "tc-input", type: "password", autoComplete: "current-password", value: password, onChange: (e) => setPassword(e.target.value), required: true })] }), _jsx("button", { className: "tc-btn primary", type: "submit", disabled: busy, children: busy ? "Signing in…" : "Sign in" }), passkeySupported() && _jsxs("button", { type: "button", className: "tc-btn", disabled: busy, onClick: () => void passkey(), children: ["Sign in with ", biometricName()] }), _jsxs("span", { className: "tc-auth-links", children: [_jsx("button", { type: "button", className: "tc-link", onClick: () => { setForgot(true); setError(null); }, children: "Forgotten your password?" }), device?.known && !device.needsFull && (device.pin || (device.passkey && passkeySupported())) && full && _jsx("button", { type: "button", className: "tc-link", onClick: () => { setFull(false); setError(null); }, children: "Use my PIN" })] })] })] }) }));
}
